import moment from 'moment'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { CAMPAIGN_TYPES_VALUES } from 'containers/AwarenessCenter/constants'

import {
  approveCampaignError,
  approveCampaignSuccess,
  getCampaignError,
  getCampaignSuccess,
  removeDraftError,
  removeDraftSuccess,
  saveAndApproveCampaignError,
  saveAndApproveCampaignSuccess,
  saveDraftError,
  saveDraftSuccess,
} from '../steps/actions'
import {
  GET_CAMPAIGN_ASYNC,
  INIT_EDIT_MODE,
  REMOVE_DRAFT_ASYNC,
  SAVE_AND_APPROVE_CAMPAIGN_ASYNC,
  SAVE_DRAFT_ASYNC,
} from '../steps/constants'
import landingPageStepEpics from '../steps/LandingPageStep/epics'
import scenariosStepEpics from '../steps/ScenariosStep/epics'
import { validateStepFields as validateSetupStepFields } from '../steps/SetupStep/actions'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../steps/TrainingModuleStep/constants'

import { CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA } from './constants'

const mapSaveSuccessCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftSuccess,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignSuccess,
  [CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA]: saveDraftSuccess,
}
const mapSaveErrorCallbacks = {
  [SAVE_DRAFT_ASYNC.INITIATE]: saveDraftError,
  [SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE]: saveAndApproveCampaignError,
  [CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA]: saveDraftError,
}

const getCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(INIT_EDIT_MODE, GET_CAMPAIGN_ASYNC.INITIATE)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    })
    .mergeMap((action) =>
      api.wizardCampaign
        .getCampaign(
          store.getState().partnerOptions.selectedCompany?.value,
          store.getState().awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id ||
            action.payload.id
        )
        .mergeMap((response) =>
          Observable.of(getCampaignSuccess(response), validateSetupStepFields())
        )
        .catch((error) => Observable.of(epicsErrorHandler(getCampaignError, error)))
        .takeUntil(action$.ofType(GET_CAMPAIGN_ASYNC.CANCEL))
    )

const removeDraftEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(REMOVE_DRAFT_ASYNC.INITIATE)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    })
    .mergeMap(() => {
      const id = store.getState().awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id
      const createdSpearPhishingCampaignId =
        store.getState().awarenessCenter.wizardSpearPhishingCampaign.generateEmailsStep
          .createdSpearPhishingCampaignId

      return api.awarenessCenter
        .deleteCampaign(id || createdSpearPhishingCampaignId)
        .mergeMap(({ response }) => Observable.of(removeDraftSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(removeDraftError, error)))
        .takeUntil(action$.ofType(REMOVE_DRAFT_ASYNC.CANCEL))
    })

const saveDraftEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      SAVE_DRAFT_ASYNC.INITIATE,
      SAVE_AND_APPROVE_CAMPAIGN_ASYNC.INITIATE,
      CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA
    )
    // prevent executing if user left the 'awarenessCenter' async reducer
    .filter(() => !!store.getState().awarenessCenter?.wizardSpearPhishingCampaign)
    .mergeMap((action) => {
      const campaignData =
        store.getState().awarenessCenter.wizardSpearPhishingCampaign.self.campaign
      const trainingIds = campaignData.selectedTrainingVideo.reduce((acc, { id }) => {
        if (id !== AUTOMATED_LEVEL_BASED_TRAINING.id) {
          acc.push(id)
        }

        return acc
      }, [])

      if (action.type === CHANGE_SPEAR_PHISHING_CAMPAIGN_DATA) {
        if (
          // notifyMe case
          campaignData.spearPhishingCampaignData.generateScenarios &&
          action.payload.generateScenarios &&
          action.payload.notifyGenerationFinished
        ) {
          campaignData.spearPhishingCampaignData = {
            notifyGenerationFinished: action.payload.notifyGenerationFinished,
          }
        } else {
          // start/cancel generation case
          campaignData.spearPhishingCampaignData = {
            generateScenarios: action.payload.generateScenarios,
            notifyGenerationFinished: action.payload.notifyGenerationFinished,
          }
        }
      } else {
        campaignData.spearPhishingCampaignData = {}
      }

      const createdSpearPhishingCampaignId =
        store.getState().awarenessCenter.wizardSpearPhishingCampaign.generateEmailsStep
          .createdSpearPhishingCampaignId

      return api.wizardCampaign
        .saveCampaign(store.getState().partnerOptions.selectedCompany?.value, {
          campaignId: campaignData.id || createdSpearPhishingCampaignId || undefined,
          flowType: CAMPAIGN_TYPES_VALUES.SPEAR_PHISHING,
          name: campaignData.name,
          defaultLocaleId: campaignData.defaultLocaleId,
          isTournament: campaignData.isTournament,
          participantsChoices: campaignData.participants.included,
          participantsExcludeChoices: campaignData.participants.excluded,
          timezone: campaignData.timezone,
          scheduleTime: moment(`${campaignData.launchDate} ${campaignData.launchTime}`).format(
            'YYYY-MM-DDTHH:mm'
          ),
          endDate: moment(campaignData.endDate).format('YYYY-MM-DD'),
          closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
          sendRemindersUntil: campaignData.sendRemindersUntil,
          emailLimit: campaignData.maxEmailPerDay,
          sendAllWeek: campaignData.sendAllWeek,
          landingPageId: campaignData.selectedLandingPage.id,
          trainingIds,
          deliveryType: campaignData.delivery,
          sendReminderEveryXBusinessDays: campaignData.sendReminderEveryXBusinessDays,
          spearPhishingCampaignData: campaignData.spearPhishingCampaignData,
          randomized: campaignData.randomized,
          sendCampaignFailedEmail: campaignData.sendCampaignFailedEmail,
          sendCampaignSummaryToCampaignReportRecipients:
            campaignData.sendCampaignSummaryToCampaignReportRecipients,
          notifications: campaignData.notifications,
        })
        .mergeMap(({ response }) =>
          Observable.of(mapSaveSuccessCallbacks[action.type](response, action))
        )
        .catch((error) =>
          Observable.of(epicsErrorHandler(mapSaveErrorCallbacks[action.type], error))
        )
        .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
    })

const approveCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(SAVE_AND_APPROVE_CAMPAIGN_ASYNC.SUCCESS)
    .filter(() => {
      if (store.getState().awarenessCenter.wizardSpearPhishingCampaign) return true
      return false
    })
    .mergeMap(() =>
      api.wizardCampaign
        .approveCampaign(
          store.getState().awarenessCenter.wizardSpearPhishingCampaign.self.campaign.id
        )
        .mergeMap(({ response }) => Observable.of(approveCampaignSuccess(response)))
        .catch((error) => Observable.of(epicsErrorHandler(approveCampaignError, error)))
        .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
    )

export default combineEpics(
  combineEpics(saveDraftEpic, getCampaignEpic, removeDraftEpic, approveCampaignEpic),
  scenariosStepEpics,
  landingPageStepEpics
)
